"use client";

import React, {useState} from "react";
import {Container} from "@/shared/components/shared";
import Image from "next/image";

// Slider
import {Pagination} from 'swiper/modules';
import {Swiper, SwiperSlide} from 'swiper/react';
import { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import 'swiper/css/pagination';

// Большая декоративная капля масла для фона (десктоп)
const OilDropIcon = () => (
    <svg 
        width="250" 
        height="250" 
        viewBox="0 0 400 400" 
        fill="none" 
        xmlns="http://www.w3.org/2000/svg" 
        className="absolute -bottom-10 -right-3 z-0 opacity-0 group-hover:opacity-20 transition-opacity duration-500 ease-in-out"
        
    >
        <path 
            d="M200 50C200 50 120 150 120 220C120 284.183 155.817 320 200 320C244.183 320 280 284.183 280 220C280 150 200 50 200 50Z" 
            fill="#FF6B00" 
            stroke="#FF6B00" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
        />
    </svg>
);

// Капля масла для мобильной версии (всегда видна)
const OilDropIconMobile = () => (
    <svg 
        width="200" 
        height="200" 
        viewBox="0 0 400 400" 
        fill="none" 
        xmlns="http://www.w3.org/2000/svg" 
        className="absolute top-1/2 -translate-y-1/2 -right-2 z-0 opacity-20"
        
    >
        <path 
            d="M200 50C200 50 120 150 120 220C120 284.183 155.817 320 200 320C244.183 320 280 284.183 280 220C280 150 200 50 200 50Z" 
            fill="#FF6B00" 
            stroke="#FF6B00" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
        />
    </svg>
);

interface CardProps {
    imageSrc: string;
    title: string;
    hoverText: string;
    width?: number;
    height?: number;
    showOilIcon?: boolean;
}

const Card = ({imageSrc, title, hoverText, width = 740, height = 296, showOilIcon = false}: CardProps) => {
    return (
        <div className="relative group overflow-hidden rounded-[20px] cursor-pointer w-full">
            <div className="w-full aspect-[740/296] relative flex items-center justify-center overflow-hidden">
                <Image
                    src={imageSrc}
                    alt="Карточка масла"
                    width={width}
                    height={height}
                    className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
                />

                <div
                    className="absolute inset-0 bg-gradient-to-b from-transparent from-34% to-[rgba(40,40,40,0.91)] to-94% mix-blend-multiply"/>

                <div className="absolute inset-0 z-10 flex items-end p-6">
                    <h3 className="text-white text-xl text-[32px] uppercase leading-[1.2]"
                        dangerouslySetInnerHTML={{__html: title}}/>
                </div>

                <div
                    className="absolute inset-0 z-20 bg-[#434343] transform translate-x-full transition-transform duration-500 ease-in-out group-hover:translate-x-0 overflow-hidden">
                    {showOilIcon && <OilDropIcon />}
                    <div className="p-6 lg:pt-[30px] lg:pb-[50px] lg:pl-[30px] lg:pr-[30px] h-full flex items-start relative z-10 overflow-y-auto">
                        <h3 className="text-lg lg:text-2xl text-white leading-[1.2]"
                            dangerouslySetInnerHTML={{__html: hoverText}}/>
                    </div>
                </div>
            </div>
        </div>
    )
}

const cardsData = [
    {
        imageSrc: "/images/new-level-service/oil-bar.png",
        imageSrcMobile: "/images/new-level-service/oil-bar-mobile.png",
        title: "Oil Bar с богатой<br/> картой масел",
        hoverText: "Здесь масло для вашего автомобиля подают, как в лучших барах — за стойкой, от наших «барменов»-экспертов.<br/>В меню «карты масел» всегда есть из чего выбрать и всегда в наличии любимое масло вашего автомобиля.<br/>Всё сертифицировано, проверено, и да — мы сами меняем масло здесь."
    },
    {
        imageSrc: "/images/new-level-service/pit-stop.png",
        imageSrcMobile: "/images/new-level-service/pit-stop-mobile.png",
        title: "Масляный PIT STOP —<br/> как в автоспорте",
        hoverText: "Пит-стоп - это философия максимальной эффективности. Здесь Вас ждет масляный PIT STOP, где замена жидкостей — точная и слаженная работа команды, отточенная опытом. Мастер и эксперт одновременно работают с вашим автомобилем, чтобы Вы не думали о процессе и мелочах, и получали только безупречный результат.<br/>И да, скорее всего, Вы не успеете допить ваш вкусный кофе в зоне ожидания, пока всё будет готово."
    },
    {
        imageSrc: "/images/new-level-service/mechanics-desktop.jpg",
        imageSrcMobile: "/images/new-level-service/mechanics.jpg",
        title: "Фокусировка мастеров<br/> по маркам автомобилей",
        hoverText: "Только увидели ваш автомобиль — и уже знаем: какое масло подойдет идеально, особенности вашего автомобиля и необходимый регламент обслуживания. " +
            "Это не просто экспертиза — это любовь к автомобилям, отточенная 23 годами работы."
    },
    {
        imageSrc: "/images/new-level-service/reception.png",
        imageSrcMobile: "/images/new-level-service/reception-mobile.png",
        title: "Сервис заботы и<br/> гостеприимства",
        hoverText: "Чистота «априори»<br/> Убедитесь сами: к нам можно заехать и на каблуках, и в деловом костюме.<br/>" +
            "Каждый пост как дорожка подиума. Все продумано для Вашего удобства.<br/>" +
            "Комфортные зоны ожидания, созданы для расслабленного ожидания."
    }
];

export const NewLevelService: React.FC = () => {
    const [expandedCard, setExpandedCard] = useState<number | null>(null);
    const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);

    const toggleCard = (index: number) => {
        setExpandedCard(expandedCard === index ? null : index);
    };

    const handleSlideChange = () => {
        setExpandedCard(null);
    };

    return (
        <section className={'bg-custom-black overflow-x-hidden'}>
            <Container className={'pt-13 pb-10 lg:pt-31 lg:pb-35 overflow-x-hidden'}>
                <h2 className={'text-white font-bold text-[30px] lg:text-[70px] uppercase leading-none'}>
                    Откройте для себя<br/>
                    <span className={'text-custom-orange'}>новый уровень сервиса</span>
                </h2>
                <div className={'hidden md:grid md:grid-cols-1 lg:grid-cols-2 md:gap-10 md:mt-16 overflow-x-hidden'}>
                    {cardsData.map((card, index) => (
                        <Card 
                            key={index} 
                            imageSrc={card.imageSrc} 
                            title={card.title}
                            hoverText={card.hoverText}
                            showOilIcon={index < 2}
                        />
                    ))}
                </div>
            </Container>
            <div className={'block md:hidden'}>
                <Swiper
                    onSwiper={setSwiperInstance}
                    onSlideChange={handleSlideChange}
                    pagination={{clickable: true, el: '.custom-pagination'}}
                    className="relative"
                    modules={[Pagination]}
                >
                    {cardsData.map((card, index) => (
                        <SwiperSlide key={index}>
                            <div className="w-full h-[455px] relative flex items-center justify-center" onClick={() => toggleCard(index)}>
                                <Image src={card.imageSrcMobile} alt={'карточка услуги масла'} className={'w-full'}
                                       width={390} height={455}/>
                                <div
                                    className="absolute inset-0 bg-gradient-to-b from-transparent from-34% to-[rgba(40,40,40,0.91)] to-94% mix-blend-multiply"/>

                                <div className="absolute inset-0 z-10 flex items-end p-6">
                                    <h3 className="text-white text-center w-full text-[24px] uppercase leading-[1.2]"
                                        dangerouslySetInnerHTML={{__html: card.title}}/>
                                </div>
                            </div>
                            <div className={`bg-custom-dark-gray transition-all relative duration-300 z-50 overflow-hidden ${expandedCard === index ? 'max-h-[500px]' : 'max-h-0'}`}>
                                    {index < 2 && <OilDropIconMobile />}
                                    <p className="text-[18px] text-white leading-[1.1] pt-11 pb-11 pl-5 pr-5 relative z-10"
                                       dangerouslySetInnerHTML={{__html: card.hoverText}}></p>
                            </div>
                        </SwiperSlide>
                    ))}
                </Swiper>
                <div
                    className="custom-pagination flex justify-center space-x-2 z-10 mt-12 pb-12"></div>
            </div>
        </section>
    );
}